/**
 * The films' motion: for every element a film moves or fades, the inline
 * style it takes from one frame of values (`v`, the timeline's vars). This
 * is the only place motion lives; the stylesheets hold materials and pivots.
 * A drawing never paints before its first frame (the loop or the still is
 * written before paint). Written per element, a frame restyles only what
 * changed — an inherited custom property written on a shared ancestor would
 * restyle the whole set every frame.
 *
 * Each entry: [selector, (v, ctx) => styles, prep?(el) => ctx].
 */

/* End-card values: a primitive a film never sets keeps these (the assembly
   film's cap, for one, is always printed: part-ink 1). */
export const INITIAL = {
  'cam-s': 1, 'cam-x': 800, 'cam-y': 455, 'pad-x': 484, 'pad-dy': 0, 'pad-o': 0, 'pad-skew': 0,
  'foot': 0, 'front': 0, 'cup-x': 484, 'etch-ink': 1, 'etch-res': 0, 'lip': 0, 'part-ink': 1,
  'detail': 0, 'bloom': 1, 'crop-0': 1, 'crop-1': 1, 'crop-2': 1, 'crop-3': 1, 'leader': 1,
  'label': 1, 'frame-dy': -150, 'frame-tilt': 0, 'sq-dz': -30, 'fl-dz': -30,
  'flex': 0, 'edge': -105.26, 'mesh-v': 0, 'v-w': 40, 'bead': -143.26, 'roll': 0.74,
  'coat': -105.26, 'fill': -105.26, 'print': 1, 'grip-dy': -150, 'grip': 0, 'cap-x': 1274,
  'cap-dy': -22, 'hook': 0, 'axis': 0, 'click': 0, 'nest-x': 150, 'probe-dy': 0, 'spring': 1,
  'check': 1,
}

const px = (n) => `${n.toFixed(2)}px`
const num = (n) => n.toFixed(4)
const clamp = (lo, n, hi) => Math.min(hi, Math.max(lo, n))
const clamp01 = (n) => clamp(0, n, 1)
const atCliche = (v) => (v['pad-x'] < 804 ? 1 : 0)
const shadowH = (v) => Math.max(0, 104 - v['pad-dy'])
const chainBend = (v) => (1238 + v['pad-x']) / 2
const styleNumber = (el, name) => Number.parseFloat(el.style.getPropertyValue(name)) || 0

/* The camera's scale as the labels and the camera group share it. */
const camScale = (v) => {
  const reach = num(clamp01((v['cam-s'] - 1) / 0.6))
  return `${num(v['cam-s'])} * (1 + var(--boost, 0) * ${reach})`
}
const labelAt = (v, ax, ay) =>
  `translate(calc((${ax.toFixed(2)} - ${v['cam-x'].toFixed(2)}) * ${camScale(v)} * var(--u)), ` +
  `calc((${ay.toFixed(2)} - ${v['cam-y'].toFixed(2)}) * ${camScale(v)} * var(--u))) translate(var(--tx), var(--ty))`

const PAD = [
  [
    '.pp-cam',
    (v) => ({
      transform: `translate(800px, 455px) scale(calc(${camScale(v)})) translate(${px(-v['cam-x'])}, ${px(-v['cam-y'])})`,
    }),
  ],
  ['.pp-x', (v) => ({ transform: `translateX(${px(v['pad-x'])})` })],
  ['.pp-xy', (v) => ({ transform: `translate(${px(v['pad-x'])}, ${px(v['pad-dy'])})` })],
  [
    '.pp-dome',
    (v) => ({
      transform: `translate(${px(v['pad-x'])}, ${px(346 + v['pad-dy'])}) skewX(${num(v['pad-skew'])}deg) scale(${num(1 + 0.00125 * v['pad-o'])}, ${num(1 - 0.003333 * v['pad-o'])})`,
    }),
  ],
  [
    '.pp-bulge--flat',
    (v) => ({
      transform: `translate(${px(v['pad-x'])}, ${px(550 - 10 * v.foot)}) scale(${num(v.foot)})`,
      opacity: String(atCliche(v)),
    }),
  ],
  [
    '.pp-bulge--arch',
    (v) => ({
      transform: `translate(${px(v['pad-x'] + 0.4)}, ${px(550 - 10 * v.foot)}) scale(${num(v.foot)}, ${num(v.foot * v.foot)})`,
      opacity: String(1 - atCliche(v)),
    }),
  ],
  ['.pp-front', (v) => ({ opacity: num(v.front) })],
  ['.pp-lip', (v) => ({ opacity: num(v.lip) })],
  [
    '.pp-shadow',
    (v) => {
      const he = shadowH(v) + 50
      return {
        transform: `translate(${px(v['pad-x'] + 0.3 * he)}, ${px(550 - 0.06 * he)}) scale(${num(1 - shadowH(v) / 250)})`,
        opacity: String(clamp01((60 - shadowH(v)) * 100)),
      }
    },
  ],
  ['.pp-cup', (v) => ({ transform: `translate(${px(v['cup-x'])}, 550px)` })],
  ['.pp-cup-rod', (v) => ({ transform: `translateX(253.3px) scaleX(${num(v['cup-x'] - 309.3)})` })],
  ['.pp-etch-ink', (v) => ({ opacity: num(v['etch-ink']) })],
  ['.pp-etch-res', (v) => ({ opacity: num(v['etch-res']) })],
  ['.pp-part-ink', (v) => ({ opacity: num(v['part-ink']) })],
  ['.pp-abs-l', (v) => ({ transform: `translateX(${px(clamp(-6, v['pad-x'] - 484, 6))})` })],
  ['.pp-abs-r', (v) => ({ transform: `translateX(${px(clamp(-6, v['pad-x'] - 1124, 6))})` })],
  ['.pp-chain-bend', (v) => ({ transform: `translateX(${px(chainBend(v))})` })],
  ['.pp-chain-lower', (v) => ({ clipPath: `inset(0 ${px(1520 - chainBend(v))} 0 0)` })],
  ['.pp-chain-upper', (v) => ({ clipPath: `inset(0 ${px(1520 - chainBend(v))} 0 ${px(v['pad-x'] - 362)})` })],
  ['.pp-detail-a', (v) => ({ transform: `translate(${px(v['pad-x'])}, ${px(446 + v['pad-dy'] - v['pad-o'])})` })],
  ['.pp-detail-src', (v) => ({ opacity: num(Math.min(1, v.detail * 5)) })],
  [
    '.pp-bubble',
    (v) => ({
      transform: `translate(144.7px, -104.7px) scale(${num(clamp01(v.detail * 1.25 - 0.25))}) translate(-144.7px, 104.7px)`,
    }),
  ],
  ['.pp-bloom', (v) => ({ transform: `scale(${num(v.bloom)})` })],
  [
    '.pp-crop',
    (v, el) => {
      const c = v[`crop-${el.index}`]
      return {
        transform: `translate(${px(el.dx * (1 - c))}, ${px(el.dy * (1 - c))})`,
        opacity: num(Math.min(1, c * 4)),
      }
    },
    (el) => ({
      dx: styleNumber(el, '--dx'),
      dy: styleNumber(el, '--dy'),
      index: Number((el.getAttribute('class').match(/pp-crop-(\d)/) ?? [0, 0])[1]),
    }),
  ],
  ['.pp-reg-leader', (v) => ({ clipPath: `inset(-6px ${(100 * (1 - v.leader)).toFixed(2)}% -6px -6px)` })],
  ['.pp-reg-dot', (v) => ({ opacity: String(clamp01(v.leader * 1000)) })],
  ['.pp-label--reg', (v, el) => ({ transform: labelAt(v, el.ax, el.ay) }), anchor],
  [
    '.pp-label.pp-label--detail',
    (v) => ({
      transform: labelAt(v, v['pad-x'] + 268, v['pad-dy'] - v['pad-o'] + 296),
      opacity: num(clamp01(v.detail * 1.25 - 0.25)),
    }),
  ],
  ['.pp-reg-service', (v) => ({ clipPath: `inset(0 0 ${(100 * (1 - Math.min(1, v.label * 2))).toFixed(2)}% 0)` })],
  ['.pp-reg-mark', (v) => ({ clipPath: `inset(0 0 ${(100 * (1 - clamp01(v.label * 2 - 1))).toFixed(2)}% 0)` })],
]

function anchor(el) {
  return { ax: styleNumber(el, '--ax'), ay: styleNumber(el, '--ay') }
}

const SCREEN = [
  ['.pp-head', (v) => ({ transform: `translate(${px(v['pad-x'])}, ${px(v['frame-dy'])})` })],
  [
    '.pp-frame',
    (v) => ({
      transform: `translateY(${px(v['frame-dy'])}) translate(440px, 545px) rotate(${num(v['frame-tilt'] * -0.072)}deg) translate(-440px, -545px)`,
    }),
  ],
  ['.pp-hrod-l', (v) => ({ transform: `translateY(${px(v['frame-dy'])})` })],
  ['.pp-hrod-r', (v) => ({ transform: `translateY(${px(v['frame-dy'] - v['frame-tilt'])})` })],
  ['.pp-sq', (v) => ({ transform: `translateY(${px(v['sq-dz'])})` })],
  ['.pp-fl', (v) => ({ transform: `translateY(${px(v['fl-dz'])})` })],
  ['.pp-blade-face', (v) => ({ transform: `matrix(0.18, -0.5, ${num(0.176 + 0.0184 * v.flex)}, 1, 32, 568)` })],
  ['.pp-blade-end', (v) => ({ transform: `matrix(1, 0, ${num(0.176 + 0.0184 * v.flex)}, 1, 35.96, 557)` })],
  ['.pp-vtrail', (v) => meshV(v, v['v-w'])],
  ['.pp-vlead', (v) => meshV(v, -40)],
  ['.pp-coat', (v) => ({ clipPath: `inset(0 ${px(532 - v.coat)} 0 0)` })],
  ['.pp-fill', (v) => ({ clipPath: `inset(0 ${px(532 - v.fill)} 0 0)` })],
  ['.pp-bead', (v) => ({ transform: `translateX(${px(v.bead)})` })],
  ['.pp-bead-roll', (v) => ({ transform: `translate(${px((v.roll - 6) * 0.9)}, ${px((v.roll - 6) * 0.33)})` })],
  ['.pp-print', (v) => ({ opacity: num(v.print) })],
]

/* A slope of the mesh V: from the blade edge, pressed down by mesh-v (a face
   shift of 0.36 u, 2 v per unit), across `run` to the mesh plane. */
function meshV(v, run) {
  const m = v['mesh-v']
  return {
    transform: `translateX(${px(v.edge)}) matrix(${num(run - 0.36 * m)}, ${num(-2 * m)}, 0, 1, ${num(0.36 * m)}, ${num(2 * m)})`,
    opacity: num(clamp01(m / 12)),
  }
}

const ASSEMBLY = [
  ['.pp-grip-xy', (v) => ({ transform: `translate(${px(v['pad-x'])}, ${px(v['grip-dy'])})` })],
  ['.pp-finger-l', (v) => ({ transform: `translateX(${px((1 - v.grip) * -9)})` })],
  ['.pp-finger-r', (v) => ({ transform: `translateX(${px((1 - v.grip) * 9)})` })],
  ['.pp-cap-carry', (v) => ({ transform: `translate(${px(v['cap-x'] - 1124)}, ${px(v['cap-dy'])})` })],
  ['.pp-cap-shadow', (v) => ({ opacity: num(clamp01(1 + v['cap-dy'] / 4)) })],
  ['.pp-hook-l', (v) => ({ transform: `rotate(${num(-v.hook)}deg)` })],
  ['.pp-hook-r', (v) => ({ transform: `rotate(${num(v.hook)}deg)` })],
  ['.pp-nest', (v) => ({ transform: `translateX(${px(v['nest-x'])})` })],
  ['.pp-probe-rod', (v) => ({ transform: `translate(1324px, 320px) scaleY(${num(20 + v['probe-dy'])})` })],
  ['.pp-probe-head', (v) => ({ transform: `translateY(${px(v['probe-dy'])})` })],
  ['.pp-spring', (v) => ({ transform: `translate(1324px, ${px(356 + v['probe-dy'])}) scaleY(${num(v.spring)})` })],
  ['.pp-probe-pin', (v) => ({ transform: `translateY(${px(v['probe-dy'] - 30 * (1 - v.spring))})` })],
  ['.pp-ticks, .pp-bticks', (v) => ({ opacity: num(v.click) })],
  ['.pp-tick', (v) => ({ transform: `scale(${num(0.7 + 0.3 * v.click)})` })],
  [
    '.pp-axis-line',
    (v) => ({
      clipPath: `inset(${(100 * Math.max(0, v.axis - 1)).toFixed(2)}% -6px ${(100 * Math.max(0, 1 - v.axis)).toFixed(2)}% -6px)`,
    }),
  ],
  ['.pp-check', (v) => ({ clipPath: `inset(0 -4px ${(100 * (1 - v.check)).toFixed(2)}% -4px)` })],
  [
    '.pp-bbubble',
    (v) => ({
      transform: `translate(72.3px, 34.3px) scale(${num(clamp01(v.detail * 1.25 - 0.25))}) translate(-72.3px, -34.3px)`,
    }),
  ],
  ['.pp-bcap', (v) => ({ transform: `translateY(${px((v['cap-dy'] + 22) * 4.3)})` })],
  ['.pp-bhook', (v) => ({ transform: `translate(2.5px, -62px) rotate(${num(-v.hook)}deg) translate(-2.5px, 62px)` })],
]

export const MOVERS = [...PAD, ...SCREEN, ...ASSEMBLY]
